import { Control, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLanguage } from '@/contexts/LanguageContext';
import type { ExamFormData } from '../utils/examValidation';
import { ageInMonths, readIndicators, type Indicator, type PersonInput } from '@/lib/assessment/namatAssessment';
import { IndicatorBadge } from '@/components/health/IndicatorBadge';
import { ACTIVITY_DURATIONS, ACTIVITY_FREQUENCIES, ACTIVITY_INTENSITIES } from '@/lib/assessment/fitnessAge';

/** The three activity questions behind the computed fitness age (#56), in the form's field names. */
const ACTIVITY_QUESTIONS = [
  { name: 'activityFrequency', key: 'frequency', codes: ACTIVITY_FREQUENCIES },
  { name: 'activityDuration', key: 'duration', codes: ACTIVITY_DURATIONS },
  { name: 'activityIntensity', key: 'intensity', codes: ACTIVITY_INTENSITIES },
] as const;

interface PhysicalAssessmentProps {
  control: Control<ExamFormData>;
  setValue?: (name: keyof ExamFormData, value: number) => void;
  /** Birth date and sex; children 2-19 are classified by CDC percentile (#90). */
  person?: PersonInput;
}

export function PhysicalAssessment({ control, setValue, person }: PhysicalAssessmentProps) {
  const { t } = useLanguage();
  const watched = useWatch({
    control,
    name: [
      'bmi',
      'hba1c',
      'cholesterol',
      'bloodPressure',
      'pulse',
      'waistCircumference',
      'neckCircumference',
      'activityFrequency',
      'activityDuration',
      'activityIntensity',
    ],
  });
  // Each indicator's level as soon as it is typed in (#91), age at today;
  // fitness age is computed from waist, pulse and the activity answers (#56).
  const ageMonths = person?.birthDate ? ageInMonths(person.birthDate, new Date()) : null;
  const readings = readIndicators(
    {
      bmi: watched[0],
      hba1c: watched[1],
      cholesterol: watched[2],
      bloodPressure: watched[3],
      pulse: watched[4],
      waistCircumference: watched[5],
      neckCircumference: watched[6],
      activityFrequency: watched[7],
      activityDuration: watched[8],
      activityIntensity: watched[9],
    },
    { ageMonths, sex: person?.sex }
  );
  const fitness = readings.find((r) => r.key === 'fitnessAge');
  const fitnessAgeText = fitness
    ? fitness.bound
      ? t(`dashboard.examiner.examination.physical.fitnessAge.${fitness.bound}`, { value: fitness.value })
      : String(fitness.value)
    : '';
  const levelOf = (key: Indicator) => {
    const reading = readings.find((r) => r.key === key);
    return reading ? <IndicatorBadge reading={reading} t={t} /> : null;
  };

  // Watch height and weight for BMI calculation
  const watchedHeight = useWatch({ control, name: 'height' });
  const watchedWeight = useWatch({ control, name: 'weight' });

  // Calculate BMI automatically
  useEffect(() => {
    if (watchedHeight && watchedWeight && watchedHeight > 0 && watchedWeight > 0 && setValue) {
      const heightInMeters = watchedHeight / 100; // Convert cm to meters
      const bmi = watchedWeight / (heightInMeters * heightInMeters);
      const roundedBMI = Math.round(bmi * 100) / 100; // Round to 2 decimal places
      setValue('bmi', roundedBMI);
    }
  }, [watchedHeight, watchedWeight, setValue]);

  return (
    <div className="max-h-[60vh] overflow-y-auto pr-2 space-y-6">
      {/* Body Measurements */}
    <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">{t('dashboard.examiner.examination.physical.sections.bodyMeasurements')}</h3>
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          name="height"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('dashboard.examiner.examination.physical.height')}</FormLabel>
              <FormControl>
                <Input 
                  type="number" 
                  {...field} 
                  onChange={e => field.onChange(Number(e.target.value))}
                  placeholder={t('dashboard.examiner.examination.physical.heightPlaceholder')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="weight"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('dashboard.examiner.examination.physical.weight')}</FormLabel>
              <FormControl>
                <Input 
                  type="number" 
                  {...field} 
                  onChange={e => field.onChange(Number(e.target.value))}
                  placeholder={t('dashboard.examiner.examination.physical.weightPlaceholder')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

          <FormField
            control={control}
            name="bodyFatPercentage"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dashboard.examiner.examination.physical.bodyFatPercentage')}</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.1"
                    {...field} 
                    onChange={e => field.onChange(Number(e.target.value))}
                    placeholder={t('dashboard.examiner.examination.physical.bodyFatPercentagePlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="muscleMass"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dashboard.examiner.examination.physical.muscleMass')}</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.1"
                    {...field} 
                    onChange={e => field.onChange(Number(e.target.value))}
                    placeholder={t('dashboard.examiner.examination.physical.muscleMassPlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="waistCircumference"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dashboard.examiner.examination.physical.waistCircumference')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.1"
                    {...field}
                    onChange={e => field.onChange(Number(e.target.value))}
                    placeholder={t('dashboard.examiner.examination.physical.waistCircumferencePlaceholder')}
                  />
                </FormControl>
                <FormDescription>{t('dashboard.examiner.examination.physical.waistCircumferenceHint')}</FormDescription>
                {levelOf('waistCircumference')}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="neckCircumference"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dashboard.examiner.examination.physical.neckCircumference')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.1"
                    {...field}
                    onChange={e => field.onChange(Number(e.target.value))}
                    placeholder={t('dashboard.examiner.examination.physical.neckCircumferencePlaceholder')}
                  />
                </FormControl>
                <FormDescription>{t('dashboard.examiner.examination.physical.neckCircumferenceHint')}</FormDescription>
                {levelOf('neckCircumference')}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Metabolic Indicators */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">{t('dashboard.examiner.examination.physical.sections.metabolicIndicators')}</h3>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={control}
            name="bmi"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dashboard.examiner.examination.physical.bmi')} (Auto-calculated)</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.01"
                    {...field} 
                    readOnly
                    className="bg-gray-50"
                    placeholder={t('dashboard.examiner.examination.physical.bmiPlaceholder')}
                  />
                </FormControl>
                {levelOf('bmi')}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="bmr"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dashboard.examiner.examination.physical.bmr')}</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    {...field} 
                    onChange={e => field.onChange(Number(e.target.value))}
                    placeholder={t('dashboard.examiner.examination.physical.bmrPlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Blood Tests */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">{t('dashboard.examiner.examination.physical.sections.bloodTests')}</h3>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={control}
            name="hba1c"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dashboard.examiner.examination.physical.hba1c')}</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.1"
                    {...field} 
                    onChange={e => field.onChange(Number(e.target.value))}
                    placeholder={t('dashboard.examiner.examination.physical.hba1cPlaceholder')}
                  />
                </FormControl>
                {levelOf('hba1c')}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="glucoseLevel"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dashboard.examiner.examination.physical.glucoseLevel')}</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    {...field} 
                    onChange={e => field.onChange(Number(e.target.value))}
                    placeholder={t('dashboard.examiner.examination.physical.glucoseLevelPlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="triglycerides"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dashboard.examiner.examination.physical.triglycerides')}</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    {...field} 
                    onChange={e => field.onChange(Number(e.target.value))}
                    placeholder={t('dashboard.examiner.examination.physical.triglyceridesPlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="cholesterol"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dashboard.examiner.examination.physical.cholesterol')}</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    {...field} 
                    onChange={e => field.onChange(Number(e.target.value))}
                    placeholder={t('dashboard.examiner.examination.physical.cholesterolPlaceholder')}
                  />
                </FormControl>
                {levelOf('cholesterol')}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Vital Signs */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">{t('dashboard.examiner.examination.physical.sections.vitalSigns')}</h3>
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          name="bloodPressure.systolic"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('dashboard.examiner.examination.physical.bloodPressure.systolic')}</FormLabel>
              <FormControl>
                <Input 
                  type="number" 
                  {...field} 
                  onChange={e => field.onChange(Number(e.target.value))}
                  placeholder={t('dashboard.examiner.examination.physical.bloodPressure.systolicPlaceholder')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="bloodPressure.diastolic"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('dashboard.examiner.examination.physical.bloodPressure.diastolic')}</FormLabel>
              <FormControl>
                <Input 
                  type="number" 
                  {...field} 
                  onChange={e => field.onChange(Number(e.target.value))}
                  placeholder={t('dashboard.examiner.examination.physical.bloodPressure.diastolicPlaceholder')}
                />
              </FormControl>
              {levelOf('bloodPressure')}
                <FormMessage />
            </FormItem>
          )}
        />

          <FormField
            control={control}
            name="pulse"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dashboard.examiner.examination.physical.pulse')}</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    {...field} 
                    onChange={e => field.onChange(Number(e.target.value))}
                    placeholder={t('dashboard.examiner.examination.physical.pulsePlaceholder')}
                  />
                </FormControl>
                {levelOf('pulse')}
                <FormMessage />
              </FormItem>
            )}
          />

      <FormField
        control={control}
            name="oxygenLevel"
        render={({ field }) => (
          <FormItem>
                <FormLabel>{t('dashboard.examiner.examination.physical.oxygenLevel')}</FormLabel>
            <FormControl>
              <Input 
                type="number" 
                {...field} 
                onChange={e => field.onChange(Number(e.target.value))}
                    placeholder={t('dashboard.examiner.examination.physical.oxygenLevelPlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
                 </div>
       </div>

      {/* Physical activity: the three questions behind the computed fitness age (#56) */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">{t('dashboard.examiner.examination.physical.sections.physicalActivity')}</h3>
        <div className="grid grid-cols-2 gap-4">
          {ACTIVITY_QUESTIONS.map((q) => (
            <FormField
              key={q.name}
              control={control}
              name={q.name}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t(`dashboard.examiner.examination.physical.activity.${q.key}.label`)}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value ?? ''}>
                    <FormControl>
                      <SelectTrigger data-testid={`select-${q.name}`}>
                        <SelectValue placeholder={t('dashboard.examiner.examination.physical.activity.choose')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {q.codes.map((code) => (
                        <SelectItem key={code} value={code}>
                          {t(`dashboard.examiner.examination.physical.activity.${q.key}.${code}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
        </div>
        <div className="flex items-center gap-3 rounded-md border p-3" data-testid="fitness-age-preview">
          {fitness ? (
            <>
              <span className="text-sm font-medium">
                {t('dashboard.examiner.examination.physical.fitnessAge.value', { fitnessAge: fitnessAgeText, age: fitness.realAge ?? '' })}
              </span>
              <IndicatorBadge reading={fitness} t={t} />
            </>
          ) : (
            <span className="text-sm text-muted-foreground">{t('dashboard.examiner.examination.physical.fitnessAge.pending')}</span>
          )}
        </div>
      </div>

       {/* Laboratory Tests */}
       <div className="space-y-4">
         <h3 className="text-lg font-medium text-gray-900">{t('dashboard.examiner.examination.physical.sections.laboratoryTests')}</h3>
         <div className="grid grid-cols-2 gap-4">
           {/* ABG Tests */}
           <FormField
             control={control}
             name="ph"
             render={({ field }) => (
               <FormItem>
                 <FormLabel>{t('dashboard.examiner.examination.physical.ph')}</FormLabel>
                 <FormControl>
                   <Input 
                     type="number" 
                     step="0.01"
                     {...field} 
                     onChange={e => field.onChange(Number(e.target.value))}
                     placeholder={t('dashboard.examiner.examination.physical.phPlaceholder')}
                   />
                 </FormControl>
                 <FormMessage />
               </FormItem>
             )}
           />

           <FormField
             control={control}
             name="pco2"
             render={({ field }) => (
               <FormItem>
                 <FormLabel>{t('dashboard.examiner.examination.physical.pco2')}</FormLabel>
                 <FormControl>
                   <Input 
                     type="number" 
                     step="0.1"
                     {...field} 
                     onChange={e => field.onChange(Number(e.target.value))}
                     placeholder={t('dashboard.examiner.examination.physical.pco2Placeholder')}
                   />
                 </FormControl>
                 <FormMessage />
               </FormItem>
             )}
           />

           <FormField
             control={control}
             name="po2"
             render={({ field }) => (
               <FormItem>
                 <FormLabel>{t('dashboard.examiner.examination.physical.po2')}</FormLabel>
                 <FormControl>
                   <Input 
                     type="number" 
                     step="0.1"
                     {...field} 
                     onChange={e => field.onChange(Number(e.target.value))}
                     placeholder={t('dashboard.examiner.examination.physical.po2Placeholder')}
                   />
                 </FormControl>
                 <FormMessage />
               </FormItem>
             )}
           />

           <FormField
             control={control}
             name="hco3"
             render={({ field }) => (
               <FormItem>
                 <FormLabel>{t('dashboard.examiner.examination.physical.hco3')}</FormLabel>
                 <FormControl>
                   <Input 
                     type="number" 
                     step="0.1"
                     {...field} 
                     onChange={e => field.onChange(Number(e.target.value))}
                     placeholder={t('dashboard.examiner.examination.physical.hco3Placeholder')}
                   />
                 </FormControl>
                 <FormMessage />
               </FormItem>
             )}
           />

           <FormField
             control={control}
             name="baseExcess"
             render={({ field }) => (
               <FormItem>
                 <FormLabel>{t('dashboard.examiner.examination.physical.baseExcess')}</FormLabel>
                 <FormControl>
                   <Input 
                     type="number" 
                     step="0.1"
                     {...field} 
                     onChange={e => field.onChange(Number(e.target.value))}
                     placeholder={t('dashboard.examiner.examination.physical.baseExcessPlaceholder')}
                   />
                 </FormControl>
                 <FormMessage />
               </FormItem>
             )}
           />

           {/* Other Lab Tests */}
           <FormField
             control={control}
             name="sodium"
             render={({ field }) => (
               <FormItem>
                 <FormLabel>{t('dashboard.examiner.examination.physical.sodium')}</FormLabel>
                 <FormControl>
                   <Input 
                     type="number" 
                     step="0.1"
                     {...field} 
                     onChange={e => field.onChange(Number(e.target.value))}
                     placeholder={t('dashboard.examiner.examination.physical.sodiumPlaceholder')}
                   />
                 </FormControl>
                 <FormMessage />
               </FormItem>
             )}
           />

           <FormField
             control={control}
             name="potassium"
             render={({ field }) => (
               <FormItem>
                 <FormLabel>{t('dashboard.examiner.examination.physical.potassium')}</FormLabel>
                 <FormControl>
                   <Input 
                     type="number" 
                     step="0.1"
                     {...field} 
                     onChange={e => field.onChange(Number(e.target.value))}
                     placeholder={t('dashboard.examiner.examination.physical.potassiumPlaceholder')}
                   />
                 </FormControl>
                 <FormMessage />
               </FormItem>
             )}
           />

           <FormField
             control={control}
             name="chloride"
             render={({ field }) => (
               <FormItem>
                 <FormLabel>{t('dashboard.examiner.examination.physical.chloride')}</FormLabel>
                 <FormControl>
                   <Input 
                     type="number" 
                     step="0.1"
                     {...field} 
                     onChange={e => field.onChange(Number(e.target.value))}
                     placeholder={t('dashboard.examiner.examination.physical.chloridePlaceholder')}
                   />
                 </FormControl>
                 <FormMessage />
               </FormItem>
             )}
           />

           <FormField
             control={control}
             name="calcium"
             render={({ field }) => (
               <FormItem>
                 <FormLabel>{t('dashboard.examiner.examination.physical.calcium')}</FormLabel>
                 <FormControl>
                   <Input 
                     type="number" 
                     step="0.1"
                     {...field} 
                     onChange={e => field.onChange(Number(e.target.value))}
                     placeholder={t('dashboard.examiner.examination.physical.calciumPlaceholder')}
                   />
                 </FormControl>
                 <FormMessage />
               </FormItem>
             )}
           />

           <FormField
             control={control}
             name="urea"
             render={({ field }) => (
               <FormItem>
                 <FormLabel>{t('dashboard.examiner.examination.physical.urea')}</FormLabel>
                 <FormControl>
                   <Input 
                     type="number" 
                     step="0.1"
                     {...field} 
                     onChange={e => field.onChange(Number(e.target.value))}
                     placeholder={t('dashboard.examiner.examination.physical.ureaPlaceholder')}
                   />
                 </FormControl>
                 <FormMessage />
               </FormItem>
             )}
           />

           <FormField
             control={control}
             name="creatinine"
             render={({ field }) => (
               <FormItem>
                 <FormLabel>{t('dashboard.examiner.examination.physical.creatinine')}</FormLabel>
                 <FormControl>
                   <Input 
                     type="number" 
                     step="0.01"
                     {...field} 
                     onChange={e => field.onChange(Number(e.target.value))}
                     placeholder={t('dashboard.examiner.examination.physical.creatininePlaceholder')}
                   />
                 </FormControl>
                 <FormMessage />
               </FormItem>
             )}
           />

           <FormField
             control={control}
             name="troponin"
             render={({ field }) => (
               <FormItem>
                 <FormLabel>{t('dashboard.examiner.examination.physical.troponin')}</FormLabel>
                 <FormControl>
                   <Input 
                     type="number" 
                     step="0.001"
                     {...field} 
                     onChange={e => field.onChange(Number(e.target.value))}
                     placeholder={t('dashboard.examiner.examination.physical.troponinPlaceholder')}
                   />
                 </FormControl>
                 <FormMessage />
               </FormItem>
             )}
           />

           <FormField
             control={control}
             name="hemoglobin"
             render={({ field }) => (
               <FormItem>
                 <FormLabel>{t('dashboard.examiner.examination.physical.hemoglobin')}</FormLabel>
                 <FormControl>
                   <Input 
                     type="number" 
                     step="0.1"
                     {...field} 
                     onChange={e => field.onChange(Number(e.target.value))}
                     placeholder={t('dashboard.examiner.examination.physical.hemoglobinPlaceholder')}
                   />
                 </FormControl>
                 <FormMessage />
               </FormItem>
             )}
           />

           <FormField
             control={control}
             name="uricAcid"
             render={({ field }) => (
               <FormItem>
                 <FormLabel>{t('dashboard.examiner.examination.physical.uricAcid')}</FormLabel>
                 <FormControl>
                   <Input 
                     type="number" 
                     step="0.1"
                     {...field} 
                     onChange={e => field.onChange(Number(e.target.value))}
                     placeholder={t('dashboard.examiner.examination.physical.uricAcidPlaceholder')}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
         </div>
       </div>
    </div>
  );
}